/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { PropTypes } from 'prop-types';
import { withRouter } from 'react-router-dom';
import { Field, withFormik } from 'formik';
import {
  Form as SemanticForm, Grid, Dimmer, Loader,
} from 'semantic-ui-react';
import moment from 'moment';

import { InputFormField } from '@playbookux/playbook-web-core';

import withDashboardSidebar from '../../components/with-dashboard-sidebar';

import SelectFormField from '../../../core/components/select-form-field';
import InputDatePickerField from '../../../core/components/input-datepicker-field';
import ButtonSelectFormField from '../../../core/components/button-select-form-field';

import {
  annualHouseholdIncomeOptions,
  countryOptions,
  genderOptions,
  raceEthnicHeritageOptions,
  stateOptions,
} from '../../../tester/pages/sign-up-survey/options';

import {
  Container,
  SelectsContainer,
  BottomButton,
  ContainerButtons,
  Typography,
  ErrorMessage,
  ContainerInput,
} from './styles';
import { getUser, updateUser } from './graphql';

const TesterPersonalForm = (props) => {
  const {
    status, values, history, match, setValues, handleSubmit,
  } = props;

  const [error, setError] = useState(false);

  const ageTester = (birthdate) => `${moment().diff(birthdate, 'years')}`;

  const setBirthdate = (birthdate, age) => {
    setValues({ ...values, birthdate, age });
  };

  const handleSetAge = (fieldName, birthdate) => {
    if (!birthdate) {
      setBirthdate('', '');
      return;
    }
    setBirthdate(birthdate, ageTester(birthdate));
  };

  const handleBirthdate = (e, { value }) => {
    if (!value) {
      setBirthdate('', '');
      return;
    }
    const age = parseInt(value, 10);
    const date = moment().subtract(age, 'years');
    setBirthdate(new Date(date), age);
  };

  useEffect(() => {
    getUser(match.params.id)
      .then((result) => {
        const {
          country,
          state,
          gender,
          birthdate,
          raceEthnicHeritage,
          annualHouseholdIncome,
          firstName,
          lastName,
        } = result.user.profile;

        setValues({
          country,
          state,
          gender,
          birthdate: birthdate ? new Date(birthdate) : '',
          age: birthdate ? ageTester(birthdate) : '',
          raceEthnicHeritage,
          annualHouseholdIncome,
          firstName,
          lastName,
        });
      })
      .catch((err) => setError(err));
  }, []);

  if (error) {
    return (
      <Container>
        <ErrorMessage>Error while retrieving the tester.</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      {status && status.isLoading && (
        <Dimmer active={status.isLoading} inverted>
          <Loader inverted data-loader="tester-personal-loader" />
        </Dimmer>
      )}
      {status && status.error && (
        <Typography>
          Error while sending profile information. Please try again.
        </Typography>
      )}

      <SemanticForm as="div">
        <SemanticForm.Field>
          <Field
            labelFontSize="14px"
            labelText="First Name"
            name="firstName"
            component={InputFormField}
          />
        </SemanticForm.Field>
        <SemanticForm.Field>
          <Field
            labelFontSize="14px"
            labelText="Last Name"
            name="lastName"
            component={InputFormField}
          />
        </SemanticForm.Field>
        <SemanticForm.Field>
          <Field
            name="gender"
            render={(prop) => (
              <ButtonSelectFormField
                {...prop}
                items={genderOptions}
                label="Which gender do you identify with?"
                render={(item) => <p>{item.text}</p>}
              />
            )}
          />
        </SemanticForm.Field>

        <SemanticForm.Field>
          <Field
            name="raceEthnicHeritage"
            render={(prop) => (
              <ButtonSelectFormField
                {...prop}
                items={raceEthnicHeritageOptions}
                label="What is your ethnicity?"
                render={(item) => <p>{item.text}</p>}
              />
            )}
          />
        </SemanticForm.Field>

        <ContainerInput>
          <SemanticForm.Field>
            <Field
              labelFontSize="14px"
              labelText="Birthdate"
              name="birthdate"
              component={InputDatePickerField}
              onChange={handleSetAge}
              maxDate={new Date()}
            />
          </SemanticForm.Field>

          <SemanticForm.Field>
            <Field
              labelFontSize="14px"
              labelText="Age"
              name="age"
              onChange={handleBirthdate}
              component={InputFormField}
            />
          </SemanticForm.Field>
        </ContainerInput>

        <SemanticForm.Field>
          <Field
            name="annualHouseholdIncome"
            render={(prop) => (
              <ButtonSelectFormField
                {...prop}
                items={annualHouseholdIncomeOptions}
                label="What is your annual household income?"
                render={(item) => <p>{item.text}</p>}
              />
            )}
          />
        </SemanticForm.Field>

        <SelectsContainer>
          <SemanticForm.Field>
            <Field
              label="Country"
              name="country"
              placeholder="Select"
              isMulti={false}
              isCreatable={false}
              options={countryOptions}
              labelFontSize="18px"
              labelColor="#172B4D"
              component={SelectFormField}
            />
          </SemanticForm.Field>
          {values.country === 'us' && (
            <SemanticForm.Field>
              <Field
                label="State"
                name="state"
                placeholder="Select"
                isMulti={false}
                isCreatable={false}
                options={stateOptions}
                labelFontSize="18px"
                labelColor="#172B4D"
                component={SelectFormField}
              />
            </SemanticForm.Field>
          )}
        </SelectsContainer>
      </SemanticForm>

      <ContainerButtons>
        <Grid centered>
          <Grid.Row centered columns={3}>
            <Grid.Column>
              <Grid centered>
                <BottomButton
                  circular
                  onClick={() => history.push('/testers')}
                  type="submit"
                >
                  Cancel
                </BottomButton>
              </Grid>
            </Grid.Column>
            <Grid.Column>
              <Grid centered>
                <BottomButton
                  next
                  circular
                  type="submit"
                  onClick={handleSubmit}
                >
                  Send
                </BottomButton>
              </Grid>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </ContainerButtons>
    </Container>
  );
};

TesterPersonalForm.propTypes = {
  values: PropTypes.shape({
    country: PropTypes.string,
    birthdate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    age: PropTypes.string,
  }).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  status: PropTypes.shape({
    isLoading: PropTypes.bool,
    error: PropTypes.bool,
  }),
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
};

TesterPersonalForm.defaultProps = {
  status: {
    error: false,
  },
};

export default compose(
  withDashboardSidebar,
  withRouter,
  withFormik({
    mapPropsToValues: () => ({
      firstName: '',
      lastName: '',
      birthdate: '',
      age: '',
      gender: '',
      raceEthnicHeritage: '',
      annualHouseholdIncome: '',
      country: '',
      state: '',
    }),
    country: '',
    handleSubmit: (
      {
        firstName,
        lastName,
        birthdate,
        gender,
        country,
        raceEthnicHeritage,
        annualHouseholdIncome,
        ...values
      },
      { props, setStatus },
    ) => {
      const newValues = values;
      if (!!values.country && values.country.toLowerCase() !== 'us') {
        newValues.state = '';
      }

      const attributes = {
        firstName,
        lastName,
        birthdate,
        gender,
        raceEthnicHeritage,
        annualHouseholdIncome,
        country,
        state: newValues.state,
      };

      setStatus({
        isLoading: true,
      });

      updateUser(props.match.params.id, attributes)
        .then(() => {
          setStatus({
            isLoading: false,
          });
          props.history.push('/testers');
        })
        .catch(() => {
          setStatus({
            isLoading: false,
            error: true,
          });
        });
    },
  }),
)(TesterPersonalForm);
